import { type MouseEvent as ReactMouseEvent, useMemo, useState } from 'react';
import { browserToPdf } from '../../SignaturePlacement/PlacementCanvas/placement.utils.ts';
import type { PageDimensionsPt } from '../../SignaturePlacement/PlacementCanvas/PlacementCanvas.types.ts';
import type { DrawState, FieldBindingRes, PdfBox } from './FieldBindingCanvas.types.ts';

// Minimum drag distance (px) before a mousedown-drag on empty page space is
// treated as "draw a new box" rather than a stray click/misclick.
const MIN_DRAW_SIZE_PX = 6;

interface UseFieldBindingCanvasArgs {
	bindings: FieldBindingRes[];
	scale: number;
	onAddBox: (pageNum: number, pdfBox: PdfBox) => void;
}

// Owns the canvas's local render/draw state: rendered page dimensions, the
// grouped-by-page bindings, and the "draw a new box" gesture
// (mousedown/move/up). Kept out of the component so the JSX reads as pure
// layout over these values.
export const useFieldBindingCanvas = ({ bindings, scale, onAddBox }: UseFieldBindingCanvasArgs) => {
	const [pageDims, setPageDims] = useState<Map<number, PageDimensionsPt>>(new Map());
	const [draw, setDraw] = useState<DrawState | null>(null);

	const bindingsByPage = useMemo(() => {
		const grouped = new Map<number, FieldBindingRes[]>();
		bindings.forEach((binding) => {
			const list = grouped.get(binding.pageNum) ?? [];
			list.push(binding);
			grouped.set(binding.pageNum, list);
		});
		return grouped;
	}, [bindings]);

	const onPageRenderSuccess = (
		pageNum: number,
		page: { originalWidth: number; originalHeight: number }
	) => {
		setPageDims((prev) => {
			const existing = prev.get(pageNum);
			if (
				existing &&
				existing.widthPt === page.originalWidth &&
				existing.heightPt === page.originalHeight
			) {
				return prev;
			}
			const next = new Map(prev);
			next.set(pageNum, { widthPt: page.originalWidth, heightPt: page.originalHeight });
			return next;
		});
	};

	// Draw-a-new-box: mousedown on empty overlay space starts tracking; drag
	// updates a preview rect; mouseup (past a minimum size) commits it via
	// onAddBox. Clicks with no meaningful drag are ignored (not a new box).
	const handleOverlayMouseDown =
		(pageNum: number) => (event: ReactMouseEvent<HTMLDivElement>) => {
			if (event.target !== event.currentTarget) {
				return;
			}
			const rect = event.currentTarget.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const y = event.clientY - rect.top;
			setDraw({ pageNum, startX: x, startY: y, currentX: x, currentY: y });
		};

	const handleOverlayMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
		if (!draw) {
			return;
		}
		const rect = event.currentTarget.getBoundingClientRect();
		setDraw({
			...draw,
			currentX: event.clientX - rect.left,
			currentY: event.clientY - rect.top,
		});
	};

	const finishDraw = () => {
		if (!draw) {
			return;
		}
		const dims = pageDims.get(draw.pageNum);
		const left = Math.min(draw.startX, draw.currentX);
		const top = Math.min(draw.startY, draw.currentY);
		const width = Math.abs(draw.currentX - draw.startX);
		const height = Math.abs(draw.currentY - draw.startY);
		if (dims && width >= MIN_DRAW_SIZE_PX && height >= MIN_DRAW_SIZE_PX) {
			const pdfBox = browserToPdf({ left, top, width, height }, dims.heightPt, scale);
			onAddBox(draw.pageNum, pdfBox);
		}
		setDraw(null);
	};

	return {
		pageDims,
		draw,
		bindingsByPage,
		onPageRenderSuccess,
		handleOverlayMouseDown,
		handleOverlayMouseMove,
		finishDraw,
	};
};
